<template>
  <a-modal
      :visible="visible"
      title="新增通知公告"
      @cancel="handleCancel"
      :width="700"
  >
    <a-form
        ref="formRef"
        :model="formData"
        layout="vertical"
        :rules="rules"
    >
      <!-- 公告标题 -->
      <a-form-item
          label="公告标题"
          name="title"
      >
        <a-input
            v-model:value="formData.title"
            placeholder="请输入公告标题"
            :maxLength="100"
            showCount
        />
      </a-form-item>

      <!-- 公告内容 -->
      <a-form-item
          label="公告内容"
          name="content"
      >
        <a-textarea
            v-model:value="formData.content"
            placeholder="请输入公告内容"
            :rows="8"
            :maxLength="2000"
            showCount
        />
      </a-form-item>

      <!-- 附件上传 -->
      <a-form-item label="附件上传">
        <div class="upload-container">
          <uploadButton
              class="pl64"
              :updateTitle="'点击上传'"
              ref="uploadRef"
              accept=".doc,.docx,.pdf,.jpg,.png,.xls,.xlsx"
              :maxCount="1"
              pathName="announcement"
              @uploadFinished="uploadFinished"
              :info="fileList[1]" :title="'通知公告附件'">
          </uploadButton>
          <p class="upload-tip">支持PDF、Word、Excel格式文件，单个文件大小不超过10MB</p>
        </div>
      </a-form-item>

      <!-- 发布状态 -->
      <a-form-item
          label="发布状态"
          name="status"
      >
        <a-radio-group v-model:value="formData.status">
          <a-radio value="1">立即发布</a-radio>
          <a-radio value="0">草稿</a-radio>
        </a-radio-group>
      </a-form-item>
    </a-form>

    <!-- 自定义底部按钮 -->
    <template #footer>
      <div style="text-align: right;">
        <a-button @click="handleCancel">取消</a-button>
        <a-button type="primary" @click="handleSubmit" style="margin-left: 8px;">确认</a-button>
      </div>
    </template>
  </a-modal>
</template>
<script setup>
import { ref, reactive, defineProps, defineEmits, watch } from "vue";
import { message } from 'ant-design-vue'
import request from "@/utils/request";
import { Upload } from 'ant-design-vue';
import uploadButton from '@/components/uploadButton';

// 定义props
const props = defineProps({
  visible: {
    type: Boolean,
    default: false
  },
  deptId: {
    type: String,
    default: ''
  }
});

// 定义emits
const emit = defineEmits(['success', 'cancel']);

// 表单相关
const formRef = ref();
const fileList = ref([]);

// 表单数据
const formData = reactive({
  title: '',
  content: '',
  status: '1', // 默认立即发布
  attachmentUrl: '',
  fileName: ''
});

// 表单验证规则
const rules = {
  title: [
    { required: true, message: '请输入公告标题', trigger: 'blur' },
    { max: 100, message: '标题长度不能超过100个字符', trigger: 'blur' }
  ],
  content: [
    { required: true, message: '请输入公告内容', trigger: 'blur' },
    { max: 2000, message: '内容长度不能超过2000个字符', trigger: 'blur' }
  ]
};

// 重置表单
const resetForm = () => {
  formData.title = '';
  formData.content = '';
  formData.status = '1';
  formData.attachmentUrl = '';
  formData.fileName = '';
  fileList.value = [];
  if (formRef.value) {
    formRef.value.resetFields();
  }
};

const uploadRef = ref()
let uploadFileUrl = ref();
const uploadFinished = (res) => {
  console.log('上传成功', res);
  uploadFileUrl.value = res
}

// 文件上传前校验
const beforeUpload = (file) => {
  const isLessThan10M = file.size / 1024 / 1024 < 10;
  if (!isLessThan10M) {
    message.error('文件大小不能超过10MB!');
    return Upload.LIST_IGNORE;
  }
  return false; // 阻止自动上传
}

// 处理文件上传变化
const handleUploadChange = ({ fileList: newFileList }) => {
  fileList.value = newFileList;
}

// 上传文件
const uploadFile = async () => {
  if (fileList.value.length === 0) {
    return true;
  }

  const file = fileList.value[0].originFileObj;
  const uploadFormData = new FormData();
  uploadFormData.append('file', file);

  try {
    const res = await request({
      //url: '/cloud/minio/uploadOneFile',
      url: '/cloud/file/upload',
      method: 'post',
      data: uploadFormData,
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });

    if (res.code === 200) {
      formData.attachmentUrl = res.data.fileUrl;
      formData.fileName = file.name;
      return true;
    } else {
      message.error('文件上传失败');
      return false;
    }
  } catch (error) {
    message.error('文件上传出错');
    console.error('文件上传出错:', error);
    return false;
  }
}

// 提交表单
const handleSubmit = async () => {
  // 表单验证
  if (!formRef.value) return;

  try {
    await formRef.value.validate();

    // 上传文件
    const uploadResult = await uploadFile();
    if (!uploadResult) return;

    // 准备提交数据
    const submitData = {
      title: formData.title,
      content: formData.content,
      status: formData.status,
      attachmentUrl: formData.attachmentUrl,
      fileName: formData.fileName,
      deptId: props.deptId
    };

    // 调用新增公告API
    const res = await request({
      url: '/cloud/announcementEntity/insertOne',
      method: 'post',
      data: submitData
    });

    if (res.code === 200) {
      message.success('新增公告成功');
      emit('success');
      // 重置表单
      resetForm();
    } else {
      message.error(res.message || '新增公告失败');
    }
  } catch (error) {
    message.error('新增公告出错');
    console.error('新增公告出错:', error);
  }
}

// 取消新增
const handleCancel = () => {
  // 重置表单
  resetForm();
  emit('cancel');
}

watch(
    () => props.visible,
    (newValue) => {
      if (!newValue) {
        // 当弹框关闭时自动重置表单
        resetForm();
      }
    }
);

</script>

<style lang="less" scoped>
.upload-container {
  margin-top: 8px;
}

.upload-tip {
  color: #999;
  font-size: 12px;
  margin-top: 8px;
}
</style>